import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Dashboard from "./pages/Dashboard";
import AddPurchase from "./pages/AddPurchase";
import PurchaseDetail from "./pages/PurchaseDetail";
import AuthCallback from "./pages/AuthCallback";
import Wishlist from "./pages/Wishlist";
import WishlistDetail from "./pages/WishlistDetail";
import Vault from "./pages/Vault";
import AddVaultItem from "./pages/AddVaultItem";
import VaultItemDetail from "./pages/VaultItemDetail";
import Pricing from "./pages/Pricing";
import BillingCallback from "./pages/BillingCallback";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import About from "./pages/About";
import FAQPage from "./pages/FAQPage";
import AdminAnalytics from "./pages/AdminAnalytics";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import AuthGoogleCallback from "./pages/AuthGoogleCallback";
import RetailerLanding from "./pages/RetailerLanding";
import Retailers from "./pages/Retailers";
import Tour from "./pages/Tour";
import SavePage from "./pages/SavePage";
import Purchases from "./pages/Purchases";
import LaunchFreeBanner from "./components/LaunchFreeBanner";
import ErrorBoundary from "./components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import ChatWidget from "./components/ChatWidget";
import { Toaster } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm font-mono-pg">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: handle session_id BEFORE other routing
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/billing/callback" element={<BillingCallback />} />
      <Route path="/auth/google/callback" element={<AuthGoogleCallback />} />
      <Route path="/about" element={<About />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/tour" element={<Tour />} />
      <Route path="/track/:retailerSlug" element={<RetailerLanding />} />
      <Route path="/save/:slug" element={<SavePage />} />
      <Route path="/retailers" element={<Retailers />} />
      <Route path="/admin" element={<AdminAnalytics />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/dashboard/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
      <Route path="/dashboard/wishlist/:id" element={<ProtectedRoute><WishlistDetail /></ProtectedRoute>} />
      <Route path="/dashboard/vault" element={<ProtectedRoute><Vault /></ProtectedRoute>} />
      <Route path="/dashboard/vault/new" element={<ProtectedRoute><AddVaultItem /></ProtectedRoute>} />
      <Route path="/dashboard/vault/:id" element={<ProtectedRoute><VaultItemDetail /></ProtectedRoute>} />
      <Route path="/dashboard/add" element={<ProtectedRoute><AddPurchase /></ProtectedRoute>} />
      <Route path="/dashboard/purchases" element={<ProtectedRoute><Purchases /></ProtectedRoute>} />
      <Route path="/dashboard/purchase/:id" element={<ProtectedRoute><PurchaseDetail /></ProtectedRoute>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  const [launchFree, setLaunchFree] = useState(false);
  useEffect(() => {
    axios.get(`${API_BASE}/billing/plans`).then((r) => setLaunchFree(r.data?.launch_free === true)).catch(() => {});
  }, []);
  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <LaunchFreeBanner visible={launchFree} />
            <AppRouter />
            <ChatWidget />
            <Toaster position="bottom-left" theme="dark" />
          </BrowserRouter>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}
